/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import lowerCase from 'lodash/lowerCase';
import cx from 'classnames';

import {
    DropdownMenu,
    Stack,
    Alert,
    VisualOnlyLabel,
    InputError,
    announce,
} from 'sarsaparilla';

import GuestCounterRow from './GuestCounterRow';
import { isSanGabriel } from '../../constants';

const propTypes = {
    disableTextInput: PropTypes.bool,
    title: PropTypes.string,
    minTotalTitle: PropTypes.string,
    maxTotalTitle: PropTypes.string,
    guestTypes: PropTypes.array,
    minTotalGuests: PropTypes.number,
    maxTotalGuests: PropTypes.number,
    onCountChange: PropTypes.func,
    onClose: PropTypes.func,
    value: PropTypes.shape({}),
    validationErrorClass: PropTypes.string,
    disabled: PropTypes.bool,
    isDisabled: PropTypes.bool,
    maxTotalMessage: PropTypes.string,
    minTotalMessage: PropTypes.string,
    minimumCountByGuestType: PropTypes.shape({}),
    maximumCountByGuestType: PropTypes.shape({}),
    id: PropTypes.string,
    className: PropTypes.string,
    isLabelVisible: PropTypes.bool,
    isRequired: PropTypes.bool,
    isInvalid: PropTypes.bool,
    errorText: PropTypes.string,
    placeholder: PropTypes.string,
    acceptsPasses: PropTypes.bool,
    customGuestAlertText: PropTypes.string,
    ticketCountByGuestTypeId: PropTypes.object,
    passesByGuestTypeId: PropTypes.object,
    facilityId: PropTypes.string,
};

const defaultProps = {
    id: 'guest-counter',
    isLabelVisible: false,
    disableTextInput: false,
    isRequired: false,
    isInvalid: false,
    title: 'Guests',
    minTotalTitle: '',
    maxTotalTitle: '',
    disabled: false,
    isDisabled: false,
    minTotalGuests: 0,
    maxTotalGuests: 999,
    validationErrorClass: '',
    guestTypes: [
        {
            key: 'adult',
            type: 'Adult',
            description: 'ages 13+',
        },
        {
            key: 'youth',
            type: 'Youth',
            description: 'ages 9 - 12',
        },
        {
            key: 'access',
            type: 'Access',
            description: 'no age required',
        },
        {
            key: 'senior',
            type: 'Senior',
            description: 'ages 65+',
        },
    ],
    maximumCountByGuestType: {},
    minimumCountByGuestType: {},
    className: '',
    acceptsPasses: false,
    customGuestAlertText: '',
    facilityId: '',
};

export const getWeight = (guestType) => {
    if (typeof guestType.weight === 'number') {
        return guestType.weight;
    }
    return 1;
};

export default class GuestCounter extends React.Component {
    constructor(props) {
        super(props);
        this.closeButtonRef = React.createRef();
    }

    state = {
        counts: {},
    };

    componentDidMount() {
        window.addEventListener('keydown', this.handleKeyDown);
        this.setState({
            counts: this.props.value || this.populateCounters(this.props.guestTypes),
        });
    }

    componentDidUpdate(prevProps) {
        const { value, guestTypes } = this.props;

        if (value !== prevProps.value || guestTypes !== prevProps.guestTypes) {
            this.setState({
                counts: value || this.populateCounters(guestTypes),
            });
        }
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleKeyDown);
    }

    closeCounter = () => {
        const { onClose } = this.props;

        if (onClose) {
            onClose(this.state.counts, this.sumCounts());
        }
    };

    handleKeyDown = (event) => {
        const expanded = document
            .getElementById(this.props.id)
            .getAttribute('aria-expanded');

        if (event.key === 'Escape' && expanded) {
            this.closeCounter();
        }

        if (
            event.key === 'Tab' &&
            expanded &&
            this.closeButtonRef.current?.contains(event.target)
        ) {
            event.preventDefault();
            document.getElementById(this.props.id).focus();
        }
    };

    populateCounters(guestTypes) {
        const counts = {};

        if (guestTypes.length > 0) {
            guestTypes.forEach((guestType) => {
                if (guestType.count) {
                    counts[guestType.key] = guestType.count;
                } else {
                    counts[guestType.key] = 0;
                }
            });
        }

        return counts;
    }

    generateGuestCounterRows = (maxTotalGuestsReached) => {
        const counts = this.props.value || this.state.counts;
        const { disableTextInput } = this.props;

        return this.props.guestTypes.map((type) => {
            let guestType = { ...type };
            // min/max guest selection for guest type
            const max = get(this.props, `maximumCountByGuestType.${guestType.key}`, 0);
            const min = get(this.props, `minimumCountByGuestType.${guestType.key}`, 0);
            // this method renders every time count changes so validation for count input
            // is properly done here, rather than onCountChange
            const disableIncrement =
                maxTotalGuestsReached || (max !== 0 && max <= counts[guestType.key]);
            // TODO: commenting this out unless a hard req comes in to display desc/max/min
            guestType = {
                ...guestType,
                description: ``,
            };
            /*if (max > 0 && min > 0) {
                // if guest type has both a min and a max, include both in the description
                // in format: "max. #, min. #"
                guestType = {
                    ...guestType,
                    description: `${guestType.description} max. ${max}, min. ${min}`,
                };
            } else if (min > 0) {
                // if guest type has only a min, include both in the description
                // in format: " min. #"
                guestType = {
                    ...guestType,
                    description: `${guestType.description} min. ${min}`,
                };
            } else if (max > 0) {
                // if guest type has only a max, include both in the description
                // in format: "max. #, min. #"
                guestType = {
                    ...guestType,
                    description: `${guestType.description} max. ${max}`,
                };
            }*/
            return (
                <GuestCounterRow
                    id={this.props.id}
                    disableTextInput={disableTextInput}
                    key={`guest-counter-${guestType.key}`}
                    guestType={guestType}
                    count={counts[guestType.key]}
                    isDecreaseDisabled={min >= counts[guestType.key]}
                    maxTotalGuestsReached={disableIncrement}
                    changeCount={this.changeCount}
                />
            );
        });
    };

    changeCount = (key, count) => {
        const { onCountChange, maximumCountByGuestType } = this.props;
        const uncontrolled = !this.props.value;
        // max guests allowed in total
        const maxTotalGuests = this.props.maxTotalGuests || 999;
        // max guests allowed for current guest type
        const maxForGuestType =
            maximumCountByGuestType && maximumCountByGuestType[key]
                ? maximumCountByGuestType[key]
                : null;
        let counts;

        // Grab the current state
        if (uncontrolled) {
            counts = this.state.counts;
        } else {
            counts = { ...this.props.value };
        }

        // validating input here in the case a user using the keyboard instead
        // of the increment button
        const totalSelectedGuests = this.sumCounts() - counts[key];
        const totalRemaining = maxTotalGuests - totalSelectedGuests;

        // Don't let the new count go over either (a) the total or
        // (b) the guest - type specific maximums
        if (!maxForGuestType && count <= totalRemaining) {
            counts[key] = count;
        } else if (
            maxForGuestType &&
            count <= maxForGuestType &&
            count <= totalRemaining
        ) {
            counts[key] = count;
        } else if (maxForGuestType && count > maxForGuestType) {
            counts[key] = maxForGuestType;
            announce(
                `Error: Input changed to ${maxForGuestType} because there's a maximum of ${maxTotalGuests} guests allowed for the selected guest type.`
            );
        } else {
            counts[key] = totalRemaining;
            announce(
                `Error: Input changed to ${totalRemaining} because there's a maximum of ${maxTotalGuests} guests.`
            );
        }
        // Don't let counts go negative
        if (counts[key] < 0) {
            counts[key] = 0;
        }

        if (uncontrolled) {
            // Update the state
            this.setState({
                counts,
            });
        }

        if (onCountChange) {
            onCountChange(counts);
        }
    };

    sumCounts = () => {
        const counts = this.props.value || this.state.counts;
        return this.props.guestTypes.reduce((total, guestType) => {
            return total + counts[guestType.key] * getWeight(guestType);
        }, 0);
    };

    determinePluralLabel(count) {
        // Grab the title from props
        const title = this.props.title;

        // If the count is 1, and the title is pluralized, remove the s
        // This is obviously a very basic implementation
        if (count === 1 && title.charAt(title.length - 1).toLowerCase() === 's') {
            if (title.toLowerCase().endsWith('passes')) {
                // if title ends with "passes" (e.g. Vehicle Passes), remove the trailing "es"
                return title.slice(0, title.length - 2);
            }
            return title.slice(0, title.length - 1);
        }

        // Otherwise, return the plural title
        return title;
    }

    isHaveAPassAlertVisible = () => {
        const { acceptsPasses, ticketCountByGuestTypeId } = this.props;
        if (!acceptsPasses || isEmpty(ticketCountByGuestTypeId)) return false;

        const { passesByGuestTypeId } = this.props;

        const guestTypes = Object.keys(ticketCountByGuestTypeId);

        let showAlert = true;

        guestTypes.forEach((guestType) => {
            if (
                ticketCountByGuestTypeId[guestType] >= 1 &&
                isEmpty(passesByGuestTypeId[guestType])
            ) {
                showAlert = false;
            }
        });

        return showAlert;
    };

    render() {
        const {
            className,
            id,
            title,
            validationErrorClass,
            maxTotalGuests,
            isLabelVisible,
            isRequired,
            isInvalid,
            errorText,
            placeholder,
            customGuestAlertText,
            facilityId,
        } = this.props;

        // Generate Label for Dropdown
        let label = placeholder || `Add ${title}...`;
        const totalCount = this.sumCounts();
        if (totalCount > 0) {
            label = `${totalCount} ${this.determinePluralLabel(totalCount)}`;
        }

        let hasCountError = false;

        // If maxTotalGuests is passed in, check it against totalCount
        let maxTotalGuestsReached = false;
        if (maxTotalGuests !== 0 && totalCount >= maxTotalGuests) {
            maxTotalGuestsReached = true;
        }

        // Format the max title
        const maxTotalTitle =
            this.props.maxTotalTitle !== ''
                ? ` ${this.props.maxTotalTitle}`
                : ` ${lowerCase(this.props.title)}`;

        // If minTotalGuests is passed in, check it against totalCount
        const { minTotalGuests } = this.props;
        let minTotalGuestsNotReached = false;
        if (minTotalGuests !== 0 && totalCount < minTotalGuests) {
            minTotalGuestsNotReached = true;
            hasCountError = true;
        }

        // Format the min title
        const minTotalTitle = this.props.minTotalTitle
            ? ` ${lowerCase(this.props.minTotalTitle)}`
            : ` ${lowerCase(this.props.title)}`;

        const maxTotalMessage = isEmpty(this.props.maxTotalMessage)
            ? `You can select a maximum of ${this.props.maxTotalGuests} ${maxTotalTitle}.`
            : this.props.maxTotalMessage;

        const minTotalMessage = isEmpty(this.props.minTotalMessage)
            ? `You must select a minimum of ${this.props.minTotalGuests} ${minTotalTitle}.`
            : this.props.minTotalMessage;

        return (
            <div
                data-component="GuestCounter"
                role={isLabelVisible ? 'group' : null}
                aria-labelledby={isLabelVisible ? `group-label-${id}` : null}
                aria-describedby={cx({
                    [`${id}-error`]: !!errorText,
                })}
                className={cx(className, validationErrorClass)}
            >
                <Stack space="xxs">
                    {isLabelVisible && (
                        <VisualOnlyLabel id={`group-label-${id}`} isRequired={isRequired}>
                            {`Select ${title}`}
                        </VisualOnlyLabel>
                    )}

                    <DropdownMenu
                        isDisabled={this.props.disabled || this.props.isDisabled}
                        label={label}
                        applyButtonLabel="Close"
                        id={id}
                        className={cx({
                            'is-placeholder': totalCount === 0,
                            'is-invalid': hasCountError || isInvalid || errorText,
                        })}
                        onClose={this.closeCounter}
                        applyButtonRef={this.closeButtonRef}
                    >
                        <div className="sarsa-guest-counter-width-sizer">
                            <Stack space="sm">
                                {(minTotalGuestsNotReached || maxTotalGuestsReached) && (
                                    <Alert type="info" aria-live="polite">
                                        {minTotalGuestsNotReached && minTotalMessage}
                                        {maxTotalGuestsReached && maxTotalMessage}
                                    </Alert>
                                )}
                                {!!customGuestAlertText && (
                                    <Alert type="warning" aria-live="polite">
                                        You must purchase a Primary Vehicle ticket in
                                        order to add Secondary Vehicle tickets.
                                    </Alert>
                                )}
                                {isSanGabriel(facilityId) && (
                                    <Alert type="warning" aria-live="polite">
                                        See Overview section below for more details.
                                    </Alert>
                                )}
                                {this.isHaveAPassAlertVisible() && (
                                    <Alert type="info" aria-live="polite">
                                        Own a pass? Apply your pass during checkout
                                    </Alert>
                                )}
                                {this.generateGuestCounterRows(maxTotalGuestsReached)}
                            </Stack>
                        </div>
                    </DropdownMenu>
                    {!!errorText && <InputError id={id}>{errorText}</InputError>}
                </Stack>
            </div>
        );
    }
}

GuestCounter.propTypes = propTypes;
GuestCounter.defaultProps = defaultProps;
